import { useCallback } from "react";
import type { BusinessBillingInfo, BusinessBillingInfoUpdate } from "lens-platform-sdk";
import { LensSDKException, NotFoundException } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAnalytics } from "./useAnalytics";
import { queryClient } from "src/queryClient";

/**
 * Get business billing information
 */
export const useBusinessBillingInformation = (businessId: string | undefined) => {
  const lensPlatformClient = useLensPlatformClient();
  const { track } = useAnalytics();

  // See https://tkdodo.eu/blog/react-query-and-type-script#type-safety-with-the-enabled-option
  const fetchBillingInfo = useCallback(
    (id?: string) =>
      typeof id === "undefined"
        ? Promise.reject(new Error("Invalid businessId"))
        : lensPlatformClient.business.getBusinessBillingInformation(id).catch(() => null),
    [lensPlatformClient],
  );
  const patchBillingInfo = useCallback(
    (id: string, update: BusinessBillingInfoUpdate) =>
      typeof id === "undefined"
        ? Promise.reject(new Error("Invalid businessId"))
        : lensPlatformClient.business.updateBusinessBillingInformation(id, update).catch((error) => {
            const message = error instanceof Error || error?.message ? error.message : "";

            if (error.errorCode === 402) {
              const details = error.rawException?.data?.details || { message: "Something went wrong" };

              throw new LensSDKException(error.errorCode, details.message, {
                ...details,
                updateBillingInfo: update,
              });
            }

            if (message.startsWith("Unexpected exception")) {
              throw new Error("Something went wrong. Please try again later.");
            }

            throw error;
          }),
    [lensPlatformClient],
  );

  const {
    isLoading: loading,
    error,
    data: billingInfo,
  } = useQuery<BusinessBillingInfo | null, LensSDKException>({
    queryKey: ["businessBillingInfo", businessId, fetchBillingInfo],
    queryFn: () => fetchBillingInfo(businessId),
    retry: (_, error) => {
      if (error instanceof NotFoundException) {
        return false;
      }

      return true;
    },
    enabled: Boolean(businessId),
  });

  const {
    isPending: updating,
    isSuccess: updateSuccess,
    error: errorUpdating,
    mutate: update,
  } = useMutation<BusinessBillingInfo | undefined | null, LensSDKException, BusinessBillingInfoUpdate>({
    mutationFn: async (updateBillingInfo?: BusinessBillingInfoUpdate) => {
      if (!businessId || typeof updateBillingInfo === "undefined") {
        return undefined;
      }

      const updated = await patchBillingInfo(businessId, updateBillingInfo);

      return updated;
    },

    onSuccess: async () => {
      track("BusinessID updated", { businessId });
      await queryClient.invalidateQueries({ queryKey: ["businessBillingInfo", businessId] });
    },
  });

  return { error, billingInfo, loading, updating, updateSuccess, errorUpdating, update };
};
